/**
 * Operation-catalog query keys.
 *
 * One key, and it carries nothing — unlike the trial balance's, which is keyed
 * on locale and the closing-entries reading because both are part of the
 * *request*. This read takes no parameters at all: the catalog carries both
 * locales on every row, and searching and filtering happen over the cached list.
 *
 * `all` is the prefix every editor write invalidates against. That is deliberate
 * rather than lazy: a publish changes a catalog row's version and effective
 * date, a block changes its status chip, a save changes its who-may-record
 * chips — so a targeted invalidation would be bookkeeping with no payoff over a
 * seventeen-row list.
 */
export const OperationsQueryKeys = {
    all: ['operations'] as const,
    catalog: () => [...OperationsQueryKeys.all, 'catalog'] as const,
    /**
     * The tenant's category set. Carries nothing for the same reason `catalog`
     * does — both locales travel on every row, so a language switch re-renders
     * rather than refetching.
     */
    categories: () => [...OperationsQueryKeys.all, 'categories'] as const,
    /**
     * Keyed on the template's `key`, which is what the URL carries too — so
     * opening the editor for a second operation is a different query rather than
     * a refetch of the first.
     */
    template: (key: string) =>
        [...OperationsQueryKeys.all, 'template', key] as const,
    /**
     * The Record tab's picker, keyed on the date it was resolved for. Moving the
     * date is a different question, not a stale answer to the same one — a
     * template whose next version starts tomorrow answers differently for
     * tomorrow.
     */
    recordable: (date: string) =>
        [...OperationsQueryKeys.all, 'recordable', date] as const,
    /**
     * A posting preview, keyed on the **whole request body**.
     *
     * Every field of it — the date, the amount, each slot's account — changes
     * what would post, so every field belongs in the key. Keying on the
     * operation alone would serve the previous amount's entry under the current
     * one's form, which is the single most dangerous thing this screen could
     * show: the user would approve figures the record call is not going to
     * reproduce.
     */
    preview: (key: string, body: unknown) =>
        [...OperationsQueryKeys.all, 'preview', key, body] as const,
    /**
     * A page of the Activity register, keyed on the **whole filter object**.
     *
     * Every field of it changes which rows come back, so every field belongs in
     * the key. The page number is in there too, which is what lets
     * `keepPreviousData` hold page 1 on screen while page 2 loads rather than
     * blanking the register between them.
     */
    activity: (filters: unknown) =>
        [...OperationsQueryKeys.all, 'activity', filters] as const,
    /**
     * One recorded operation, keyed on the record **and the locale**: the
     * account names on its lines are resolved server-side, so switching language
     * is a different answer rather than a stale one.
     */
    activityDetail: (recordId: string, locale: string) =>
        [
            ...OperationsQueryKeys.all,
            'activity',
            'detail',
            recordId,
            locale,
        ] as const,
    /**
     * A template's configuration history. No locale in the key: the response
     * carries both locales and resolves no account names, so a language switch
     * re-renders rather than refetching.
     */
    history: (key: string) =>
        [...OperationsQueryKeys.all, 'history', key] as const,
};
